import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AudienceProviderEnum, ChannelScopeEnum } from '@/features/broadcasters/types';
import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';

import { TAudienceForm } from '../types';

import { audiencesQueryKeys } from './query-keys';

export type TPatchAudiencesParams = {
  file: File;
  provider: AudienceProviderEnum;
  scope: ChannelScopeEnum;
  country_code: string;
  region_code: string;
  subregion_code: string;
  start_datetime: Date | null;
};

export async function patchAudiences(
  body: TPatchAudiencesParams,
  onProgress: (progress: number) => void
): Promise<TAudienceForm> {
  const formData = new FormData();
  formData.append('file', body.file);
  formData.append('provider', body.provider);
  formData.append('scope', body.scope);
  formData.append('country_code', body.country_code);
  // multipart carries no null, so an empty geographic code is omitted rather than
  // sent as ''. The endpoint reads it as `request.data.get(...) or None`, so an
  // omitted field is the way to make it arrive as null.
  if (body.region_code) formData.append('region_code', body.region_code);
  if (body.subregion_code) formData.append('subregion_code', body.subregion_code);
  if (body.start_datetime) formData.append('start_datetime', body.start_datetime.toISOString());

  return mediaManagerClient.patch('/api/v1/audiences/files', formData, {
    onUploadProgress: progressEvent => {
      if (progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });
}

type MutationFn = (
  body: TPatchAudiencesParams,
  onProgress: (progress: number) => void
) => Promise<TAudienceForm>;

type TUsePatchAudiencesParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchAudience({ config = {} }: TUsePatchAudiencesParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation({
    ...config,
    mutationFn: (body: TPatchAudiencesParams) => patchAudiences(body, setUploadProgress),
    onError(error) {
      addErrorNotification(t('No se ha podido cargar el archivo'));
      console.error(error);
    },
    onSuccess() {
      addSuccessNotification(t('El archivo se ha cargado correctamente'));
      queryClient.invalidateQueries({ queryKey: audiencesQueryKeys.all });
      setUploadProgress(0);
    },
  });

  return { ...mutation, uploadProgress };
}
